import { GridApi, GridOptions, GridReadyEvent } from "ag-grid-enterprise";
import { TableParent } from "@bitwarden/web-vault/app/models/types/table.types";

import type { GridSizeChangedEvent } from "ag-grid-community/dist/types/core/events";
import { EarnViewTableSubscription, TableSource } from "./earn-view.table.subscription";
import { ColDef as AgColDef } from "ag-grid-community";
import { RedactionDescriptionRenderer } from "@bitwarden/web-vault/app/components/tables/earn-tables/renderers/description/description.cell-renderer";
import { RedactionPublishDateRenderer } from "@bitwarden/web-vault/app/components/tables/earn-tables/renderers/publish-date/publish-date.cell-renderer";
import { EarnTableStateEnum } from "@bitwarden/web-vault/app/models/enum/earn.enum";
import { getRowClass, getRowHeight } from "../../../tables.utils.js";
import { EarnTableView } from "@bitwarden/web-vault/app/models/types/transaction.types";
import { PublishedData } from "@bitwarden/web-vault/app/gloss/earn/create-and-publish/create-and-publish.component";
import { ConfirmationDialogService } from "@bitwarden/web-vault/app/services/confirmation/confirmation.service";
import { inject } from "@angular/core";
import { AppStateStoreService } from "@bitwarden/web-vault/app/services/store/app-state/app-state.store.service";

type TransactionRedactionTableSource = TableSource;

export interface ColDefExtended<T> extends AgColDef<T> {
  headerComponentFramework?: any;
}

export class EarnViewTable extends EarnViewTableSubscription {
  confirmationService = inject(ConfirmationDialogService);
  protected appState = inject(AppStateStoreService);
  gridApi: GridApi;
  options: GridOptions<TransactionRedactionTableSource>;
  private selectedRowIndex: number;
  /** In-flight guard against double-submits — the blocking overlay no longer blocks input (#5468). */
  private saving = false;
  columnDefinitions: ColDefExtended<TransactionRedactionTableSource>[];

  states: EarnTableStateEnum[] = [
    EarnTableStateEnum.DRAFT,
    EarnTableStateEnum.PREVIEW,
    EarnTableStateEnum.PUBLISH,
  ];

  constructor(parent: TableParent) {
    super();
    // todo clean up the column definitions... make a parent cell renderer and extend it for each item
    this.columnDefinitions = [
      {
        field: "publishedAt",
        headerName: "Publish Date",
        cellRenderer: RedactionPublishDateRenderer,
        suppressHeaderMenuButton: true,
      },

      {
        field: "description",
        headerName: "Description",
        cellRenderer: RedactionDescriptionRenderer,
        suppressHeaderMenuButton: true,
      },
      {
        field: "account",
        headerName: "Account",
        cellRenderer: RedactionDescriptionRenderer,
        suppressHeaderMenuButton: true,
      },
      {
        field: "transactionDate",
        headerName: "Date",
        cellRenderer: RedactionDescriptionRenderer,
        suppressHeaderMenuButton: true,
      },
      {
        field: "inOut",
        headerName: "In/Out",
        cellRenderer: RedactionDescriptionRenderer,
        suppressHeaderMenuButton: true,
      },
      {
        field: "currency",
        headerName: "Currency",
        cellRenderer: RedactionDescriptionRenderer,
        suppressHeaderMenuButton: true,
      },
      {
        field: "amount",
        headerName: "Amount",
        cellRenderer: RedactionDescriptionRenderer,
        suppressHeaderMenuButton: true,
      },
      {
        field: "source",
        headerName: "Source",
        cellRenderer: RedactionDescriptionRenderer,
        suppressHeaderMenuButton: true,
      },
    ];

    // todo clean up the options.. remove comments, unnecessary codes and see if you can bundle it up more

    this.options = {
      context: { parent },
      onGridReady: async (params: GridReadyEvent) => {
        this.gridApi = params.api;
      },
      onGridSizeChanged: (params: GridSizeChangedEvent) => {
        params.api.sizeColumnsToFit();
      },
      onPaginationChanged: (params) => {
        const newPageSize = params.api.paginationGetPageSize();
        this.earnService.defaultPageSize.set(newPageSize);
      },

      suppressCellFocus: true,
      suppressContextMenu: true,
      rowDragManaged: false,
      pagination: true,
      paginationPageSize: this.earnService.defaultPageSize(),
      paginationPageSizeSelector: [5, 10, 20, 50, 100],
      defaultColDef: {
        flex: 1, // makes columns auto-fit
        sortable: false, // allow sorting
        filter: false, // enable filtering by default
        resizable: true, // allow column resize
        floatingFilter: false, // ✅ adds search bar under each column name
      },
      columnDefs: this.columnDefinitions,
      getRowHeight: getRowHeight,
    };
  }

  async savePublishedReport() {
    // Ignore re-entry while a publish is in flight: startProcess() on a duplicate type only merges
    // the record, it doesn't block, and the blocking overlay is gone (#5468).
    if (this.saving) {
      return;
    }
    this.saving = true;
    try {
      const processMessage = `Saving data publication...`;
      this.appState.startProcess("sharing-data", processMessage);
      const appliedTransactions: EarnTableView[] = [];
      this.gridApi.forEachNode((node) => {
        appliedTransactions.push(node.data);
      });

      await this.earnService.publishPreviewData(appliedTransactions);

      this.confirmationService.notify(
        "success",
        "congratulations",
        "youHaveSuccessfullyPublishedYourData",
      );
    } catch (e) {
      this.confirmationService.notify("error", "darnIt", "somethingWentTerriblyWrong");
    } finally {
      this.appState.endProcess("sharing-data");
      this.saving = false;
    }
  }
  applyNewArrayToMask() {
    const appliedToArray: string[] = [];
    this.gridApi.forEachNodeAfterFilterAndSort((n) => appliedToArray.push(n.data.id));
    this.earnService.onAppliedToArrayChange(appliedToArray);
  }
}
